import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { supabase } from '../lib/supabase';
import type { CountryConfig } from '../types';

interface ConfigContextValue {
  config: CountryConfig | null;
  loading: boolean;
  error: string | null;
  countryCode: string;
  /** Convenience: config.country_name */
  countryName: string;
  /** Convenience: config object (alias for files that destructure countryConfig) */
  countryConfig: CountryConfig | null;
  /** Convenience: config.currency_code */
  currencyCode: string;
  /** Convenience: config.vat_rate */
  taxRate: number;
  formatCurrency: (amount: number) => string;
  formatDate: (date: string | Date) => string;
  formatDateTime: (date: string | Date) => string;
}

const ConfigContext = createContext<ConfigContextValue | null>(null);

const countryCode = import.meta.env.VITE_COUNTRY_CODE || 'DK';

export function ConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<CountryConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Loaded once per session and everything (VAT rate included) reads it, so a
    // single dropped request used to leave the whole session on a 0% VAT rate.
    // Retry before giving up.
    async function loadConfig() {
      const attempts = 3;
      for (let attempt = 1; attempt <= attempts; attempt++) {
        try {
          const { data, error: fetchError } = await supabase
            .from('country_config')
            .select('*')
            .eq('country_code', countryCode)
            .single();

          if (fetchError) throw fetchError;
          setConfig(data as CountryConfig);
          setError(null);
          break;
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to load country config';
          console.error(`ConfigContext (attempt ${attempt} of ${attempts}):`, message);
          if (attempt === attempts) {
            setError(message);
          } else {
            await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          }
        }
      }
      setLoading(false);
    }

    loadConfig();
  }, []);

  function formatCurrency(amount: number): string {
    if (!config) return String(amount);
    return new Intl.NumberFormat(config.locale, {
      style: 'currency',
      currency: config.currency_code,
    }).format(amount);
  }

  function formatDate(date: string | Date): string {
    if (!date) return '-';
    // YYYY-MM-DD strings (service_date etc.) are LOCAL country dates with no
    // timezone. Display them verbatim split — don't route through Date()
    // which would parse them as UTC midnight and shift in west-of-UTC tz.
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [y, m, d] = date.split('-');
      return `${d}/${m}/${y}`;
    }
    const d = new Date(date);
    if (isNaN(d.getTime())) return String(date);
    return new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      timeZone: config?.timezone || 'UTC',
    }).format(d);
  }

  function formatDateTime(date: string | Date): string {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return String(date);
    // Use formatToParts with the explicit timeZone so the rendered clock is
    // the COUNTRY clock, regardless of where the admin's browser is. The
    // previous implementation re-parsed a localised string through the
    // browser's local timezone, producing wrong values for cross-tz admins.
    const tz = config?.timezone || 'UTC';
    const parts = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
      timeZone: tz,
    }).formatToParts(d);
    const get = (t: string) => parts.find((p) => p.type === t)?.value || '00';
    return `${get('day')}/${get('month')}/${get('year')}, ${get('hour')}:${get('minute')}`;
  }

  return (
    <ConfigContext.Provider
      value={{
        config, loading, error, countryCode,
        countryName: config?.country_name ?? '',
        countryConfig: config,
        currencyCode: config?.currency_code ?? '',
        taxRate: config?.vat_rate ?? 0,
        formatCurrency, formatDate, formatDateTime,
      }}
    >
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig(): ConfigContextValue {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
}
